import { useEffect, useRef, useState } from 'react';

function hexToRgba(hex, a) {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// Custom cursor that replaces the default pointer when mouse disturbance is active.
//
// Design:
//   - Circle sized to mouseRadius, styled with the particle color
//   - On hoverable elements (cursor:pointer): subtle fill at low opacity
//   - Position updated imperatively (direct DOM, no React re-render) → zero lag
//   - Only top/left are imperative; other style keys stay in JSX for React to own
//
// Props:
//   enabled  — show/hide the custom cursor
//   radius   — circle radius in px (matches mouseRadius config)
//   color    — hex color string (matches particle color)
export function MouseCursor({ enabled, radius, color }) {
  const elRef      = useRef(null);
  const [hover, setHover] = useState(false);

  // Body cursor + position tracking + hover detection
  useEffect(() => {
    if (!enabled) return;

    document.body.style.cursor = 'none';
    const el = elRef.current;

    // Imperative position update — bypasses React scheduler for true 1:1 tracking
    function onMove(e) {
      if (!el) return;
      el.style.left = `${e.clientX}px`;
      el.style.top  = `${e.clientY}px`;
    }

    // Hover detection: check computed cursor of the element under the pointer
    function onOver(e) {
      const isPointer = window.getComputedStyle(e.target).cursor === 'pointer';
      setHover(prev => (prev !== isPointer ? isPointer : prev)); // skip no-op updates
    }

    // Hide cursor when mouse leaves the viewport
    function onLeave() { if (el) el.style.visibility = 'hidden'; }
    function onEnter() { if (el) el.style.visibility = 'visible'; }

    window.addEventListener('mousemove',                    onMove, { passive: true });
    document.addEventListener('mouseover',                  onOver, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    document.documentElement.addEventListener('mouseenter', onEnter);

    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove',                    onMove);
      document.removeEventListener('mouseover',                  onOver);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      document.documentElement.removeEventListener('mouseenter', onEnter);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={elRef}
      // top/left are intentionally NOT here — they are set imperatively in onMove
      // so React never resets them during re-renders caused by hover state changes.
      style={{
        position:        'fixed',
        width:           radius * 2,
        height:          radius * 2,
        borderRadius:    '50%',
        border:          `1.5px solid ${color}`,
        backgroundColor: hover ? hexToRgba(color, 0.11) : 'transparent',
        opacity:         0.72,
        pointerEvents:   'none',
        zIndex:          9999,
        // Only color transitions — position has no transition for zero-lag tracking
        transition:      'background-color 140ms ease, border-color 200ms ease',
        transform:       'translate(-50%, -50%)',
        willChange:      'left, top',
      }}
    />
  );
}
